import type { Column, DateRangeBinding } from "./tableTypes";
import { isRangeNarrowed } from "./tableTypes";
import { defaultRange } from "./DateRangePicker";

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

interface Props<T> {
  columns: Column<T>[];
  filters?: Record<string, string>;
  onFilterChange?: (param: string, value: string) => void;
  onClearFilters?: () => void;
  dateRange?: DateRangeBinding;
}

/**
 * Shows what is currently narrowing the result set, and is the only home for
 * "Clear all". Renders nothing at all when nothing is filtered, so a freshly
 * loaded page carries no empty toolbar.
 */
export function FilterChips<T>({
  columns, filters, onFilterChange, onClearFilters, dateRange,
}: Props<T>) {
  const chips: Chip[] = [];

  for (const c of columns) {
    const spec = c.filter;
    if (!spec) continue;

    if (spec.kind === "dateRange") {
      if (dateRange && isRangeNarrowed(dateRange)) {
        chips.push({
          key: `${c.header}-range`,
          label: `${c.header}: ${dateRange.from} → ${dateRange.to}`,
          clear: () => {
            const d = defaultRange();
            dateRange.onChange(d.from, d.to);
          },
        });
      }
      continue;
    }

    if (spec.kind === "numberRange") {
      const min = filters?.[spec.minParam] ?? "";
      const max = filters?.[spec.maxParam] ?? "";
      if (min !== "") {
        chips.push({
          key: spec.minParam,
          label: `${c.header} ≥ ${min}`,
          clear: () => onFilterChange?.(spec.minParam, ""),
        });
      }
      if (max !== "") {
        chips.push({
          key: spec.maxParam,
          label: `${c.header} ≤ ${max}`,
          clear: () => onFilterChange?.(spec.maxParam, ""),
        });
      }
      continue;
    }

    const value = filters?.[spec.param] ?? "";
    if (value !== "") {
      chips.push({
        key: spec.param,
        label: `${c.header}: ${value}`,
        clear: () => onFilterChange?.(spec.param, ""),
      });
    }
  }

  if (chips.length === 0) return null;

  const clearAll = () => {
    onClearFilters?.();
    if (dateRange && isRangeNarrowed(dateRange)) {
      const d = defaultRange();
      dateRange.onChange(d.from, d.to);
    }
  };

  return (
    <div className="filter-chips">
      <span className="filter-chips-label">Filters</span>
      {chips.map((chip) => (
        <span key={chip.key} className="chip">
          {chip.label}
          <button aria-label={`Clear ${chip.label}`} onClick={chip.clear}>✕</button>
        </span>
      ))}
      <button className="chip-clear-all" onClick={clearAll}>Clear all</button>
    </div>
  );
}
